import { Injectable } from '@angular/core';
import { DefaultTranspiler, HashMap, Translation, TranspileParams, getValue, isDefined, isString } from '@jsverse/transloco';

/**
 * El `DefaultTranspiler` de Transloco 7.5.1 sustituye un `{{param}}` por vez y vuelve a escanear
 * desde el principio lo que acaba de insertar: un valor «{{reason}}» pasado como `reason` cuelga la
 * pestaña, y «{{x}}» o «{{otra-clave}}» desaparecen o muestran otra traducción (#142).
 *
 * Este transpilador sustituye todas las interpolaciones de la traducción en una sola pasada: lo que
 * se inserta (un param o una traducción referenciada) no se vuelve a leer. Lo demás es igual que en
 * el de Transloco: el param se busca con `getValue` (admite `a.b`), si no está definido se usa la
 * traducción con ese nombre, transpilada con los mismos params, y si tampoco existe queda vacío.
 * Objetos y arrays siguen por `handleObject`/`handleArray`, que vuelven a llamar a `transpile`.
 *
 * Una traducción que se referencia a sí misma, directa o en ciclo («a» → «b» → «a», también a través
 * de un array u objeto), queda vacía en el punto donde se repite, en vez de agotar la pila.
 */
@Injectable()
export class SinglePassTranspiler extends DefaultTranspiler {
    // Camino de nombres de la llamada en curso, mientras se transpila una traducción referenciada que
    // no es texto (array u objeto): sus textos siguen el mismo camino en vez de empezar uno nuevo.
    private path: Set<string> | null = null;

    override transpile({ value, params = {}, translation, key }: TranspileParams): any {
        if (!isString(value)) {
            return super.transpile({ value, params, translation, key });
        }
        return this.substitute(value, params, translation, key, this.path ?? new Set([key]));
    }

    private substitute(value: string, params: HashMap, translation: Translation, key: string, visiting: Set<string>): string {
        return value.replace(this.interpolationMatcher, (_match: string, paramValue: string) => {
            const name = paramValue.trim();
            const param = getValue(params, name);
            if (isDefined(param)) {
                return param;
            }
            const referenced = translation[name];
            if (!isDefined(referenced)) {
                return '';
            }
            if (visiting.has(name)) {
                return '';
            }
            visiting.add(name);
            try {
                return isString(referenced)
                    ? this.substitute(referenced, params, translation, key, visiting)
                    : this.transpileWithin(visiting, { params, translation, key, value: referenced });
            } finally {
                visiting.delete(name);
            }
        });
    }

    private transpileWithin(visiting: Set<string>, args: TranspileParams): any {
        const outer = this.path;
        this.path = visiting;
        try {
            return this.transpile(args);
        } finally {
            this.path = outer;
        }
    }
}

/** El transpilador que registra la app (`app.config.ts`) y que usan los specs. */
export const APP_TRANSLOCO_TRANSPILER = SinglePassTranspiler;
